import { create } from 'zustand';
import { api } from '@/lib/api-client';
import type { Baby, CreateBabyRequest } from '@/types';

const ACTIVE_BABY_STORAGE_KEY = 'alma:active-baby-id';

function readStoredActiveId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_BABY_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredActiveId(id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_BABY_STORAGE_KEY, id);
    else localStorage.removeItem(ACTIVE_BABY_STORAGE_KEY);
  } catch {
    /* swallow — quota or private mode */
  }
}

interface BabyState {
  babies: Baby[];
  activeBabyId: string | null;
  loaded: boolean;
  load: () => Promise<void>;
  create: (body: CreateBabyRequest) => Promise<Baby>;
  update: (id: string, body: Partial<CreateBabyRequest>) => Promise<Baby>;
  remove: (id: string) => Promise<void>;
  setActive: (id: string) => void;
  reset: () => void;
}

export const useBabyStore = create<BabyState>((set, get) => ({
  babies: [],
  activeBabyId: readStoredActiveId(),
  loaded: false,

  load: async () => {
    try {
      const { babies } = await api.babies.list();
      const stored = get().activeBabyId;
      // Prefer stored ID if it's still in the list, otherwise fall back to first baby.
      const activeBabyId =
        (stored && babies.some((b) => b.id === stored) ? stored : babies[0]?.id) ?? null;
      set({ babies, loaded: true, activeBabyId });
      writeStoredActiveId(activeBabyId);
    } catch (err) {
      console.error('[alma] failed to load babies:', err);
      set({ loaded: true });
    }
  },

  create: async (body) => {
    const { baby } = await api.babies.create(body);
    set((s) => {
      // Newly added baby becomes active — most-recent-first matches user intent.
      writeStoredActiveId(baby.id);
      return {
        babies: [...s.babies, baby],
        activeBabyId: baby.id,
      };
    });
    return baby;
  },

  update: async (id, body) => {
    const { baby } = await api.babies.update(id, body);
    set((s) => ({
      babies: s.babies.map((b) => (b.id === id ? baby : b)),
    }));
    return baby;
  },

  remove: async (id) => {
    await api.babies.delete(id);
    set((s) => {
      const remaining = s.babies.filter((b) => b.id !== id);
      const activeStillExists = remaining.some((b) => b.id === s.activeBabyId);
      const newActive = activeStillExists ? s.activeBabyId : remaining[0]?.id ?? null;
      writeStoredActiveId(newActive);
      return {
        babies: remaining,
        activeBabyId: newActive,
      };
    });
  },

  setActive: (id) => {
    writeStoredActiveId(id);
    set({ activeBabyId: id });
  },

  reset: () => {
    writeStoredActiveId(null);
    set({ babies: [], activeBabyId: null, loaded: false });
  },
}));

export function useActiveBaby(): Baby | null {
  const { babies, activeBabyId } = useBabyStore();
  if (!activeBabyId) return null;
  return babies.find((b) => b.id === activeBabyId) ?? null;
}
